// Global Instructions Rule Applied!

import { CompanyState, CompaniesAction } from '../../interfaces/companies.ts'
import { AccountRecordFields } from '../../data_models/account_record.ts'
import { RoundEntryFields } from '../../data_models/round_entry.ts'
import {
  SET_FILTER_OPTIONS,
  SET_COMPANIES,
  SET_ACTIVE_FILTERS,
  SET_ACTIVE_SORTING,
  CLEAR_ACTIVE_FILTERS,
  CLEAR_ACTIVE_SORTING,
  UPDATE_COMPANY_RECORD,
  ADD_COMPANY,
  APPEND_COMPANIES,
  UPDATE_COMPANY_ROUND,
  REMOVE_COMPANY_FROM_LIST
} from './constants.ts'
import { UPDATE_RECORD_BY_SEARCH } from '../../sync_engine/constants.ts'

const companiesReducer = (
  state: CompanyState = {
    filterOptions: {
      data: {}
    },
    activeFilters: [],
    activeSorting: [],
    data: [],
    hasMore: false,
    currentPage: 1
  },
  action: CompaniesAction
): CompanyState => {
  switch (action.type) {
    case SET_FILTER_OPTIONS:
      const { response: filterOptionsResponse } = action.payload

      return {
        ...state,
        filterOptions: filterOptionsResponse
      }

    case SET_COMPANIES:
      const { response: companiesResponse } = action.payload

      return {
        ...state,
        data: companiesResponse.data,
        hasMore: companiesResponse.hasMore,
        currentPage: 1
      }

    case SET_ACTIVE_FILTERS:
      const { filters: activeFilters } = action.payload

      return {
        ...state,
        activeFilters
      }

    case SET_ACTIVE_SORTING:
      const { sorting: activeSorting } = action.payload

      return {
        ...state,
        activeSorting
      }

    case CLEAR_ACTIVE_FILTERS:
      return {
        ...state,
        activeFilters: []
      }

    case CLEAR_ACTIVE_SORTING:
      return {
        ...state,
        activeSorting: []
      }

    case UPDATE_COMPANY_RECORD:
      const { companyId, updates } = action.payload

      // Check if company exists in state
      const companyExists = state.data.some((company) => company[AccountRecordFields.accounts__id] === companyId)

      // If company doesn't exist, leave it be (don't add it)
      if (!companyExists) {
        return state
      }

      // Check if updates contains the company ID field - indicates a full record refresh
      // Also check if updates has many fields (more than 5) which suggests it's a full record
      const updatesIdField = updates[AccountRecordFields.accounts__id] || updates.accounts__id || updates.id
      const updateFieldCount = Object.keys(updates).length
      const isFullRecordRefresh =
        (updatesIdField === companyId || updatesIdField === String(companyId)) && updateFieldCount > 5

      // If it's a full record refresh, fully replace the company (not merge)
      // Otherwise, merge updates for backward compatibility with partial updates
      return {
        ...state,
        data: state.data.map((company) => {
          if (company[AccountRecordFields.accounts__id] === companyId) {
            return isFullRecordRefresh ? updates : { ...company, ...updates }
          }
          return company
        })
      }

    case ADD_COMPANY:
      const { company: newCompany } = action.payload

      return {
        ...state,
        data: [newCompany, ...state.data]
      }

    case APPEND_COMPANIES:
      const { response: appendResponse } = action.payload

      return {
        ...state,
        data: [...state.data, ...appendResponse.data],
        hasMore: appendResponse.hasMore,
        currentPage: state.currentPage + 1
      }

    case UPDATE_COMPANY_ROUND:
      const { roundId: companyRoundId, updates: companyRoundUpdates } = action.payload

      return {
        ...state,
        data: state.data.map((company) => {
          // Check if company has rounds array
          const rounds = company[AccountRecordFields.prog__rounds]
          if (!rounds || !Array.isArray(rounds)) {
            return company
          }

          // Find the round with matching ID
          const roundIndex = rounds.findIndex((round: any) => round[RoundEntryFields.rounds__id] === companyRoundId)

          if (roundIndex >= 0) {
            // Update the round in the array
            const updatedRounds = [...rounds]
            updatedRounds[roundIndex] = { ...updatedRounds[roundIndex], ...companyRoundUpdates }

            // Return updated company with updated rounds array
            return {
              ...company,
              [AccountRecordFields.prog__rounds]: updatedRounds
            }
          }

          return company
        })
      }

    case REMOVE_COMPANY_FROM_LIST: {
      const { companyId: removedCompanyId } = action.payload
      const idStr = String(removedCompanyId)
      return {
        ...state,
        data: state.data.filter((company) => String(company[AccountRecordFields.accounts__id]) !== idStr)
      }
    }

    case UPDATE_RECORD_BY_SEARCH: {
      const { searchField, searchValue, updates } = action.payload

      return {
        ...state,
        data: state.data.map((record) => {
          // Use loose equality to handle string/number mismatches (e.g., "123" == 123)
          // Also convert both to string for comparison to handle UUIDs consistently
          const recordValue = record[searchField]
          const matches =
            recordValue === searchValue || String(recordValue) === String(searchValue) || recordValue == searchValue

          if (matches) {
            const filteredUpdates = Object.keys(updates).reduce(
              (acc, key) => {
                if (key in record) {
                  acc[key] = updates[key]
                }
                return acc
              },
              {} as Record<string, any>
            )

            if (Object.keys(filteredUpdates).length > 0) {
              return { ...record, ...filteredUpdates }
            }
          }
          return record
        })
      }
    }

    default:
      return state
  }
}

export default companiesReducer
export * from './company_actions.ts'
export * from './company_services.ts'
export * from './selectors.ts'
